"use client";

import { Fragment } from "react";
import { findCaseRefs } from "@/lib/caseRefs";
import type { CaseIndex } from "@/lib/caseIndex";

/**
 * Render plain text with case references ("NJA 2014 s. 684", "HFD 2016
 * ref. 37", …) as clickable links — but only when the reference resolves to a
 * case we can actually serve (present in the fetched case index). Everything
 * else stays as ordinary text. Used for judgment paragraphs and the official
 * referat summary in the Dom tab.
 */
export default function LinkedText({
  text,
  index,
  onOpenCase,
  skipRef,
}: {
  text: string;
  index: CaseIndex | null;
  onOpenCase?: (refId: string, displayRef: string) => void;
  /** Don't link the case we're already showing. */
  skipRef?: string | null;
}) {
  if (!text) return null;
  if (!index || !onOpenCase) return <>{text}</>;

  const matches = findCaseRefs(text).filter(
    (m) => m.refId !== skipRef && index.ids.has(m.refId),
  );
  if (!matches.length) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let pos = 0;
  matches.forEach((m, i) => {
    if (m.start > pos) parts.push(text.slice(pos, m.start));
    const label = text.slice(m.start, m.end);
    const refId = m.refId;
    parts.push(
      <button
        key={`${m.start}-${i}`}
        type="button"
        className="btn-link"
        onClick={() => onOpenCase(refId, index.display.get(refId) ?? label)}
      >
        {label}
      </button>,
    );
    pos = m.end;
  });
  if (pos < text.length) parts.push(text.slice(pos));
  return <>{parts.map((p, i) => <Fragment key={i}>{p}</Fragment>)}</>;
}
